import React, { useCallback, useEffect, useState } from "react";
import { Button, Card, DatePicker, Descriptions, Input, Select, Space, Table, Tag, Typography, message } from "antd";
import { ClearOutlined, ReloadOutlined, SearchOutlined } from "@ant-design/icons";
import dayjs from "dayjs";

import auditApi from "../../api/v3/auditApi";

const { RangePicker } = DatePicker;
const { Text } = Typography;

const EVENT_OPTIONS = [
    { value: "login_success", label: "登录成功" },
    { value: "login_failure", label: "登录失败" },
    { value: "captcha_required", label: "需要验证" },
    { value: "account_locked", label: "账号锁定" },
    { value: "ip_rate_limited", label: "IP 限流" },
    { value: "locked_account_attempt", label: "锁定账号尝试" },
    { value: "login_in_flight_limited", label: "并发门控" },
    { value: "trust_profile_eval", label: "信任画像评估" },
    { value: "audit_log_query", label: "审计查询" },
    { value: "audit_log_detail_view", label: "审计详情查看" },
];

const SEVERITY_OPTIONS = [
    { value: "info", label: "Info" },
    { value: "warning", label: "Warning" },
    { value: "critical", label: "Critical" },
];

const OUTCOME_OPTIONS = [
    { value: "success", label: "Success" },
    { value: "failure", label: "Failure" },
    { value: "denied", label: "Denied" },
];

const RISK_OPTIONS = [
    { value: "low", label: "低" },
    { value: "medium", label: "中" },
    { value: "high", label: "高" },
];

const severityColor = {
    info: "default",
    warning: "orange",
    critical: "red",
};

const outcomeColor = {
    success: "green",
    failure: "red",
    denied: "gold",
};

const riskColor = {
    low: "green",
    medium: "orange",
    high: "red",
};

const defaultDateRange = () => [dayjs().subtract(7, "day"), dayjs()];

const formatDateTime = (value) => (value ? dayjs(value).format("YYYY-MM-DD HH:mm:ss") : "--");

const eventLabel = (eventType) => EVENT_OPTIONS.find((item) => item.value === eventType)?.label || eventType;

const serializeRange = (range) => ({
    occurred_from: range?.[0]?.toISOString(),
    occurred_to: range?.[1]?.toISOString(),
});

function DetailPanel({ record }) {
    return (
        <div style={{ padding: "8px 0" }}>
            <Descriptions size="small" column={2} bordered>
                <Descriptions.Item label="事件 ID">{record.id}</Descriptions.Item>
                <Descriptions.Item label="写入时间">{formatDateTime(record.created_at)}</Descriptions.Item>
                <Descriptions.Item label="目标类型">{record.target_type || "--"}</Descriptions.Item>
                <Descriptions.Item label="目标 ID">{record.target_id || "--"}</Descriptions.Item>
                <Descriptions.Item label="用户标识">{record.actor_user_identifier || "--"}</Descriptions.Item>
                <Descriptions.Item label="IP">{record.actor_ip || "--"}</Descriptions.Item>
                <Descriptions.Item label="设备 ID">{record.actor_device_id || "--"}</Descriptions.Item>
            </Descriptions>
            <pre
                style={{
                    marginTop: 12,
                    marginBottom: 0,
                    padding: 12,
                    borderRadius: 8,
                    background: "#f6f8fa",
                    maxHeight: 320,
                    overflow: "auto",
                    fontSize: 12,
                }}
            >
                {JSON.stringify(record.detail || {}, null, 2)}
            </pre>
        </div>
    );
}

const AuditLogPage = () => {
    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const [pagination, setPagination] = useState({ current: 1, pageSize: 20 });
    const [filters, setFilters] = useState({
        event_type: [],
        severity: [],
        outcome: [],
        risk_level: [],
        actor_user_identifier: "",
        actor_ip: "",
        range: defaultDateRange(),
    });

    const fetchEvents = useCallback(
        async (nextPagination = pagination, nextFilters = filters) => {
            setLoading(true);
            try {
                const data = await auditApi.getAuditEvents({
                    page: nextPagination.current,
                    page_size: nextPagination.pageSize,
                    event_type: nextFilters.event_type,
                    severity: nextFilters.severity,
                    outcome: nextFilters.outcome,
                    risk_level: nextFilters.risk_level,
                    actor_user_identifier: nextFilters.actor_user_identifier,
                    actor_ip: nextFilters.actor_ip,
                    ...serializeRange(nextFilters.range),
                });
                setItems(data.items || []);
                setTotal(data.total || 0);
                setPagination(nextPagination);
            } catch (error) {
                console.error("Failed to fetch audit events:", error);
                message.error(error?.response?.data?.detail || "获取审计日志失败");
            } finally {
                setLoading(false);
            }
        },
        [filters, pagination],
    );

    useEffect(() => {
        fetchEvents();
    }, []);

    const handleSearch = () => {
        const nextPagination = { ...pagination, current: 1 };
        fetchEvents(nextPagination, filters);
    };

    const handleReset = () => {
        const nextFilters = {
            event_type: [],
            severity: [],
            outcome: [],
            risk_level: [],
            actor_user_identifier: "",
            actor_ip: "",
            range: defaultDateRange(),
        };
        const nextPagination = { current: 1, pageSize: pagination.pageSize };
        setFilters(nextFilters);
        fetchEvents(nextPagination, nextFilters);
    };

    const columns = [
        {
            title: "时间",
            dataIndex: "occurred_at",
            key: "occurred_at",
            width: 180,
            render: formatDateTime,
        },
        {
            title: "事件类型",
            dataIndex: "event_type",
            key: "event_type",
            width: 190,
            render: (value) => <Tag color="blue">{eventLabel(value)}</Tag>,
        },
        {
            title: "严重级别",
            dataIndex: "severity",
            key: "severity",
            width: 110,
            render: (value) => <Tag color={severityColor[value] || "default"}>{value}</Tag>,
        },
        {
            title: "操作者",
            key: "actor",
            width: 180,
            render: (_, record) => record.actor_display_name || record.actor_username || record.actor_user_identifier || "--",
        },
        {
            title: "IP",
            dataIndex: "actor_ip",
            key: "actor_ip",
            width: 130,
            render: (value) => value || "--",
        },
        {
            title: "结果",
            dataIndex: "outcome",
            key: "outcome",
            width: 100,
            render: (value) => <Tag color={outcomeColor[value] || "default"}>{value}</Tag>,
        },
        {
            title: "风险",
            dataIndex: "risk_level",
            key: "risk_level",
            width: 90,
            render: (value) => (value ? <Tag color={riskColor[value] || "default"}>{value}</Tag> : <Text type="secondary">--</Text>),
        },
    ];

    return (
        <div className="page-container">
            <div className="page-header-compact">
                <div className="page-title-compact">审计日志</div>
                <div className="page-action-compact">
                    <Space>
                        <Button icon={<ReloadOutlined />} onClick={() => fetchEvents()} loading={loading}>
                            刷新
                        </Button>
                    </Space>
                </div>
            </div>

            <Card className="data-card" style={{ marginBottom: 16 }}>
                <Space wrap size="middle">
                    <Select mode="multiple" allowClear placeholder="事件类型" options={EVENT_OPTIONS} value={filters.event_type} onChange={(value) => setFilters((prev) => ({ ...prev, event_type: value }))} style={{ minWidth: 240 }} />
                    <RangePicker showTime value={filters.range} onChange={(value) => setFilters((prev) => ({ ...prev, range: value }))} />
                    <Select mode="multiple" allowClear placeholder="严重级别" options={SEVERITY_OPTIONS} value={filters.severity} onChange={(value) => setFilters((prev) => ({ ...prev, severity: value }))} style={{ minWidth: 160 }} />
                    <Select mode="multiple" allowClear placeholder="结果" options={OUTCOME_OPTIONS} value={filters.outcome} onChange={(value) => setFilters((prev) => ({ ...prev, outcome: value }))} style={{ minWidth: 160 }} />
                    <Select mode="multiple" allowClear placeholder="风险" options={RISK_OPTIONS} value={filters.risk_level} onChange={(value) => setFilters((prev) => ({ ...prev, risk_level: value }))} style={{ minWidth: 140 }} />
                    <Input placeholder="用户标识" prefix={<SearchOutlined />} allowClear value={filters.actor_user_identifier} onChange={(event) => setFilters((prev) => ({ ...prev, actor_user_identifier: event.target.value }))} style={{ width: 180 }} />
                    <Input placeholder="IP / IP 段" allowClear value={filters.actor_ip} onChange={(event) => setFilters((prev) => ({ ...prev, actor_ip: event.target.value }))} style={{ width: 180 }} />
                    <Button type="primary" icon={<SearchOutlined />} onClick={handleSearch} loading={loading}>
                        查询
                    </Button>
                    <Button icon={<ClearOutlined />} onClick={handleReset}>
                        重置
                    </Button>
                </Space>
            </Card>

            <Card className="data-card">
                <Table
                    columns={columns}
                    dataSource={items}
                    rowKey="id"
                    loading={loading}
                    size="small"
                    scroll={{ x: 980 }}
                    expandable={{ expandedRowRender: (record) => <DetailPanel record={record} /> }}
                    pagination={{
                        current: pagination.current,
                        pageSize: pagination.pageSize,
                        total,
                        showSizeChanger: true,
                        pageSizeOptions: ["10", "20", "50", "100"],
                        showTotal: (count) => `共 ${count} 条记录`,
                    }}
                    onChange={(nextPagination) => {
                        const pageSize = nextPagination.pageSize || pagination.pageSize;
                        const nextState = {
                            current: pageSize === pagination.pageSize ? nextPagination.current || 1 : 1,
                            pageSize,
                        };
                        fetchEvents(nextState, filters);
                    }}
                />
            </Card>
        </div>
    );
};

export default AuditLogPage;
